/**
 * Robust file-type detection for fund request uploads (check images / receipts).
 *
 * Fund request uploads are constrained to images and PDFs (the upload dropzone
 * only accepts "an image or PDF"), so anything that isn't a PDF is treated as an
 * image. This avoids dead-ends for JPEG variants whose filename lacks a clean
 * `.jpg`/`.jpeg` extension — e.g. `.jfif` (common from Windows "Save As"),
 * query-stringed S3 keys, or files with no extension at all — which previously
 * fell through to a "Preview not available for this file type" message even
 * though they were perfectly valid images. A genuine decode failure is then
 * surfaced by the `<img onError>` handler rather than mislabeled up front.
 */

/** Strip query string / fragment and normalize case for extension checks. */
const cleanFilename = (filename?: string | null): string =>
    (filename || '').split(/[?#]/)[0].trim().toLowerCase();

/** True when the upload should be rendered with the PDF viewer. */
export const isPdfFile = (filename?: string | null): boolean =>
    cleanFilename(filename).endsWith('.pdf');

/**
 * True when the upload should be rendered as an image. For fund request uploads
 * this is "anything that isn't a PDF" — including extensionless and `.jfif`-style
 * JPEGs that the old extension allow-list missed.
 */
export const isImageFile = (filename?: string | null): boolean =>
    !isPdfFile(filename);
